/**
 * Audio Mix Configuration Component
 *
 * Allows editing the audio mixing template stored in the config table.
 * The template defines tracks with sources, offsets, and keyframes.
 *
 * @module AudioMixConfig
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  IconButton,
  Tooltip,
  Collapse,
  Paper,
} from '@mui/material';
import {
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import {
  validateMixingSpec,
  formatMixingSpecJson,
  DEFAULT_MIX_SPEC,
  type MixingValidationError,
} from '@musedrops/shared/src/mixing-utils';
import { supabase } from '../../../services/supabase';

export const AudioMixConfig: React.FC = () => {
  const [jsonValue, setJsonValue] = useState('');
  const [originalValue, setOriginalValue] = useState('');
  const [signaturePath, setSignaturePath] = useState('');
  const [originalSignaturePath, setOriginalSignaturePath] = useState('');
  const [demoTtsPath, setDemoTtsPath] = useState('');
  const [originalDemoTtsPath, setOriginalDemoTtsPath] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    MixingValidationError[]
  >([]);
  const [showHelp, setShowHelp] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Load config on mount
  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load mix spec
      const { data: mixSpecData, error: mixSpecError } = await supabase
        .from('config')
        .select('value')
        .eq('key', 'default_mixing_spec')
        .eq('category', 'audio')
        .maybeSingle();

      if (mixSpecError) {
        throw new Error(mixSpecError.message);
      }

      const value = mixSpecData?.value || DEFAULT_MIX_SPEC;
      const formatted = formatMixingSpecJson(value);
      setJsonValue(formatted);
      setOriginalValue(formatted);

      // Load signature audio path
      const { data: signatureData, error: signatureError } = await supabase
        .from('config')
        .select('value')
        .eq('key', 'signature_audio_path')
        .eq('category', 'audio')
        .maybeSingle();

      if (signatureError) {
        throw new Error(signatureError.message);
      }

      const signatureValue = (signatureData?.value as string) || '';
      setSignaturePath(signatureValue);
      setOriginalSignaturePath(signatureValue);

      // Load demo TTS path
      const { data: demoTtsData, error: demoTtsError } = await supabase
        .from('config')
        .select('value')
        .eq('key', 'demo_tts_path')
        .eq('category', 'audio')
        .maybeSingle();

      if (demoTtsError) {
        throw new Error(demoTtsError.message);
      }

      const demoTtsValue = (demoTtsData?.value as string) || '';
      setDemoTtsPath(demoTtsValue);
      setOriginalDemoTtsPath(demoTtsValue);

      setHasChanges(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load configuration'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = useCallback(
    (newValue: string) => {
      setJsonValue(newValue);
      setHasChanges(
        newValue !== originalValue ||
          signaturePath !== originalSignaturePath ||
          demoTtsPath !== originalDemoTtsPath
      );
      setSuccess(null);

      // Validate JSON syntax
      try {
        const parsed = JSON.parse(newValue);
        const errors = validateMixingSpec(parsed);
        setValidationErrors(errors);
      } catch {
        setValidationErrors([{ message: 'Invalid JSON syntax' }]);
      }
    },
    [
      originalValue,
      signaturePath,
      originalSignaturePath,
      demoTtsPath,
      originalDemoTtsPath,
    ]
  );

  const handleSignaturePathChange = (newValue: string) => {
    setSignaturePath(newValue);
    setHasChanges(
      jsonValue !== originalValue ||
        newValue !== originalSignaturePath ||
        demoTtsPath !== originalDemoTtsPath
    );
    setSuccess(null);
  };

  const handleDemoTtsPathChange = (newValue: string) => {
    setDemoTtsPath(newValue);
    setHasChanges(
      jsonValue !== originalValue ||
        signaturePath !== originalSignaturePath ||
        newValue !== originalDemoTtsPath
    );
    setSuccess(null);
  };

  const handleSave = async () => {
    if (validationErrors.length > 0) {
      setError('Please fix validation errors before saving');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const parsed = JSON.parse(jsonValue);

      // Upsert the mix spec config
      const { error: mixSpecError } = await supabase.from('config').upsert(
        {
          key: 'default_mixing_spec',
          category: 'audio',
          value: parsed,
        },
        {
          onConflict: 'key',
        }
      );

      if (mixSpecError) {
        throw new Error(mixSpecError.message);
      }

      // Upsert the signature audio path config
      const { error: signatureError } = await supabase.from('config').upsert(
        {
          key: 'signature_audio_path',
          category: 'audio',
          value: signaturePath,
          type: 'string',
        },
        {
          onConflict: 'key',
        }
      );

      if (signatureError) {
        throw new Error(signatureError.message);
      }

      // Upsert the demo TTS path config
      const { error: demoTtsError } = await supabase.from('config').upsert(
        {
          key: 'demo_tts_path',
          category: 'audio',
          value: demoTtsPath,
          type: 'string',
        },
        {
          onConflict: 'key',
        }
      );

      if (demoTtsError) {
        throw new Error(demoTtsError.message);
      }

      setOriginalValue(jsonValue);
      setOriginalSignaturePath(signaturePath);
      setOriginalDemoTtsPath(demoTtsPath);
      setHasChanges(false);
      setSuccess('Audio mix configuration saved successfully');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to save configuration'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setJsonValue(originalValue);
    setSignaturePath(originalSignaturePath);
    setDemoTtsPath(originalDemoTtsPath);
    setHasChanges(false);
    setValidationErrors([]);
  };

  const handleCopyDefault = () => {
    const formatted = formatMixingSpecJson(DEFAULT_MIX_SPEC);
    navigator.clipboard.writeText(formatted);
  };

  if (loading) {
    return <Typography color="text.secondary">Loading...</Typography>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Audio Mix Template
        </Typography>
        <Button
          size="small"
          onClick={() => setShowHelp(!showHelp)}
          endIcon={showHelp ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {showHelp ? 'Hide Help' : 'Show Help'}
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure the default audio mixing template with tracks, offsets, and
        volume keyframes. Placeholders like {'{{voice-path}}'} are replaced with
        actual URLs at runtime. Shows can override this with their own mixing
        spec.
      </Typography>

      {/* Signature Audio Path Field */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Signature Audio Path
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Path to signature audio file in Supabase storage. Use{' '}
          {'{{signature-path}}'} placeholder in mix templates.
        </Typography>
        <TextField
          fullWidth
          value={signaturePath}
          onChange={e => handleSignaturePathChange(e.target.value)}
          disabled={saving}
          placeholder="e.g., static/signature.wav"
          size="small"
        />
      </Box>

      {/* Demo TTS Path Field */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Demo TTS Path
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Path to demo TTS audio file in Supabase storage. Used for testing
          audio mixing in ShowEdit.
        </Typography>
        <TextField
          fullWidth
          value={demoTtsPath}
          onChange={e => handleDemoTtsPathChange(e.target.value)}
          disabled={saving}
          placeholder="e.g., static/demo-tts.wav"
          size="small"
        />
      </Box>

      <Collapse in={showHelp}>
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
          <Typography variant="subtitle2" gutterBottom>
            Track Properties:
          </Typography>
          <Typography variant="body2" component="div" sx={{ mb: 1 }}>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>
                <strong>id</strong>: Track identifier (required if referenced by
                another track)
              </li>
              <li>
                <strong>source</strong>: Audio URL or placeholder (required)
              </li>
              <li>
                <strong>clipStart/clipEnd</strong>: Trim range in seconds
                (negative = from end)
              </li>
              <li>
                <strong>offset</strong>: When to start - number or &quot;trackId
                anchor shift&quot;
              </li>
              <li>
                <strong>keyframes</strong>: Volume automation [time, volume,
                time, volume, ...]
              </li>
            </ul>
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            Placeholders:
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {'{{voice-path}}'} = Story voice audio (required)
            <br />
            {'{{intro-music-path}}'} = Show intro music (required)
            <br />
            {'{{outro-music-path}}'} = Show outro music (optional, track removed
            if missing)
            <br />
            {'{{music-path}}'} = Background music (optional, track removed if
            missing)
            <br />
            {'{{theme-music-path}}'} = Show theme music/instrumental (optional,
            track removed if missing)
            <br />
            {'{{announce-path}}'} = Show voice announcement (optional, track
            removed if missing)
            <br />
            {'{{signature-path}}'} = Signature audio from config (optional,
            track removed if not configured)
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            Offset Examples:
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <code>0</code> - Start at beginning
            <br />
            <code>&quot;intro start 8.5&quot;</code> - Start 8.5s after intro
            starts
            <br />
            <code>&quot;intro end -1.5&quot;</code> - Start 1.5s before intro
            ends
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <Typography variant="subtitle2">Default Template:</Typography>
            <Tooltip title="Copy default template">
              <IconButton
                size="small"
                onClick={handleCopyDefault}
                sx={{ ml: 1 }}
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>
      </Collapse>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Validation Errors:</Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {validationErrors.map(err => (
              <li key={`${err.path || 'root'}-${err.message}`}>
                {err.path ? <code>{err.path}</code> : null} {err.message}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      <TextField
        multiline
        fullWidth
        minRows={10}
        maxRows={20}
        value={jsonValue}
        onChange={e => handleChange(e.target.value)}
        disabled={saving}
        sx={{
          fontFamily: 'monospace',
          '& .MuiInputBase-input': {
            fontFamily: 'monospace',
            fontSize: '0.875rem',
          },
        }}
        error={validationErrors.length > 0}
      />

      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving || !hasChanges || validationErrors.length > 0}
          startIcon={<SaveIcon />}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>

        <Button
          variant="outlined"
          onClick={handleDiscard}
          disabled={saving || !hasChanges}
        >
          Discard Changes
        </Button>
      </Box>
    </Box>
  );
};
